import { useEffect, useMemo, useRef, useState } from "react";
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import {
  GENDERS,
  MIN_PHOTOS,
  MAX_PHOTOS,
  PROMPT_BANK,
  RADII,
  radiusLabel,
  type Gender,
  type PromptAnswer,
} from "../lib/models";

// 18+ cutoff for the birthday picker.
const maxBirthday = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
})();
const defaultBirthday = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 25);
  return d.toISOString().slice(0, 10);
})();

// Create-flow draft (ported from iOS's signupDraftV1): mirror the in-progress
// form to local storage so killing the app mid-signup can't lose it. Cleared
// once the profile is created.
const DRAFT_KEY = "signupDraftV1";
interface SignupDraft {
  name?: string; birthday?: string; headline?: string; bio?: string;
  gender?: Gender | ""; seeking?: Gender[]; prompts?: PromptAnswer[];
  radius?: number; ageMin?: number; ageMax?: number; photoURLs?: string[];
}
function loadDraft(): SignupDraft {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_KEY) ?? "{}") as SignupDraft;
  } catch {
    return {};
  }
}
const draft = loadDraft();

function ageFrom(bday: string): number {
  const d = new Date(bday);
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}

export default function ProfileSetup() {
  const { uid, profileSaved, signOutUser } = useAuth();

  const [name, setName] = useState(draft.name ?? "");
  const [birthday, setBirthday] = useState(draft.birthday ?? defaultBirthday);
  const [headline, setHeadline] = useState(draft.headline ?? "");
  const [bio, setBio] = useState(draft.bio ?? "");
  const [gender, setGender] = useState<Gender | "">(draft.gender ?? "");
  const [seeking, setSeeking] = useState<Gender[]>(draft.seeking ?? []);
  const [prompts, setPrompts] = useState<PromptAnswer[]>(
    draft.prompts?.length === 3 ? draft.prompts : [
      { prompt: "", answer: "" },
      { prompt: "", answer: "" },
      { prompt: "", answer: "" },
    ],
  );
  const [radius, setRadius] = useState(draft.radius ?? 25);
  const [ageMin, setAgeMin] = useState(draft.ageMin ?? 18);
  const [ageMax, setAgeMax] = useState(draft.ageMax ?? 99);
  const [photoURLs, setPhotoURLs] = useState<string[]>(draft.photoURLs ?? []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror every field change to the local draft (survives an app kill).
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      name, birthday, headline, bio, gender, seeking, prompts, radius, ageMin, ageMax, photoURLs,
    } satisfies SignupDraft));
  }, [name, birthday, headline, bio, gender, seeking, prompts, radius, ageMin, ageMax, photoURLs]);

  // SERVER-SIDE draft sync. iOS gives a Safari tab and the installed PWA
  // separate storage, so a signup started in Safari looks wiped when the
  // person installs and opens the app. The draft is therefore also synced to
  // signupDrafts/{uid} (they're authed by now): restore from it when local
  // storage has nothing, and mirror changes up (debounced).
  const serverSynced = useRef(false);
  useEffect(() => {
    if (!uid) return;
    let alive = true;
    void (async () => {
      try {
        const hasLocal = !!(
          draft.name || draft.headline || draft.bio || draft.gender ||
          (draft.photoURLs?.length ?? 0) > 0 ||
          draft.prompts?.some((p) => p.prompt || p.answer)
        );
        if (!hasLocal) {
          const d = (await getDoc(doc(db, "signupDrafts", uid))).data() as SignupDraft | undefined;
          if (alive && d) {
            if (d.name) setName(d.name);
            if (d.birthday) setBirthday(d.birthday);
            if (d.headline) setHeadline(d.headline);
            if (d.bio) setBio(d.bio);
            if (d.gender) setGender(d.gender);
            if (d.seeking?.length) setSeeking(d.seeking);
            if (d.prompts?.length === 3) setPrompts(d.prompts);
            if (d.radius) setRadius(d.radius);
            if (d.ageMin) setAgeMin(d.ageMin);
            if (d.ageMax) setAgeMax(d.ageMax);
            if (d.photoURLs?.length) setPhotoURLs(d.photoURLs);
          }
        }
      } catch { /* offline / rules hiccup — local draft still covers this tab */ }
      finally { if (alive) serverSynced.current = true; }
    })();
    return () => { alive = false; };
  }, [uid]);

  useEffect(() => {
    // Never push before the restore attempt finishes — an empty first render
    // must not clobber a server draft the restore is about to read.
    if (!uid || !serverSynced.current) return;
    const t = setTimeout(() => {
      void setDoc(doc(db, "signupDrafts", uid), {
        name, birthday, headline, bio, gender, seeking, prompts, radius, ageMin, ageMax, photoURLs,
      } satisfies SignupDraft, { merge: true }).catch(() => { /* best-effort */ });
    }, 800);
    return () => clearTimeout(t);
  }, [uid, name, birthday, headline, bio, gender, seeking, prompts, radius, ageMin, ageMax, photoURLs]);

  const age = ageFrom(birthday);
  const isAdult = age >= 18;
  const promptsComplete = prompts.every((p) => p.prompt && p.answer.trim());
  const formComplete =
    !!name && !!gender && seeking.length > 0 && isAdult && promptsComplete && photoURLs.length >= MIN_PHOTOS;

  function toggleSeeking(g: Gender) {
    setSeeking((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));
  }

  function setPrompt(i: number, patch: Partial<PromptAnswer>) {
    setPrompts((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }

  const takenPrompts = useMemo(() => new Set(prompts.map((p) => p.prompt).filter(Boolean)), [prompts]);

  async function onPickPhotos(files: FileList | null) {
    if (!files || !uid) return;
    setUploading(true);
    setError(null);
    try {
      const room = MAX_PHOTOS - photoURLs.length;
      for (const file of Array.from(files).slice(0, room)) {
        if (!file.type.startsWith("image/")) { setError("Please choose image files only."); continue; }
        if (file.size > 10 * 1024 * 1024) { setError("Each photo must be under 10 MB."); continue; }
        const id = crypto.randomUUID();
        const r = ref(storage, `users/${uid}/photos/${id}`);
        await uploadBytes(r, file, { contentType: file.type });
        const url = await getDownloadURL(r);
        setPhotoURLs((prev) => (prev.length < MAX_PHOTOS ? [...prev, url] : prev));
      }
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Photo upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!uid || !formComplete) return;
    setSaving(true);
    setError(null);
    try {
      await setDoc(
        doc(db, "users", uid),
        {
          displayName: name,
          headline,
          bio,
          gender,
          seeking: [...seeking].sort(),
          prompts: prompts.filter((p) => p.prompt && p.answer),
          photoURLs,
          searchRadiusMiles: radius,
          ageMin,
          ageMax,
          birthdate: Timestamp.fromDate(new Date(birthday)),
          age,
          createdAt: serverTimestamp(),
          ageRangeAdult: false, // web has no device age signal; birthday gate applies
        },
        { merge: true },
      );
      localStorage.removeItem(DRAFT_KEY); // profile created — draft done
      void deleteDoc(doc(db, "signupDrafts", uid)).catch(() => {}); // server copy too
      profileSaved(); // only advance after a confirmed write
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Couldn't create your profile. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="screen">
      <div className="profile-header">
        <h1 style={{ margin: 0 }}>Create profile</h1>
        <button className="btn-ghost-sm" style={{ margin: 0 }} onClick={() => void signOutUser()}>
          Sign out
        </button>
      </div>
      <p className="muted">This is what people see in Discover and Relay.</p>

      <label>First name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" />

      <label>Birthday {isAdult ? "" : "(must be 18+)"}</label>
      <input type="date" max={maxBirthday} value={birthday} onChange={(e) => setBirthday(e.target.value)} />

      <label>Headline</label>
      <input
        value={headline}
        maxLength={60}
        onChange={(e) => setHeadline(e.target.value)}
        placeholder="One line that sums you up"
      />

      <label>About you</label>
      <textarea value={bio} maxLength={150} onChange={(e) => setBio(e.target.value)} placeholder="What should they spend a message on?" />

      <h2>Photos ({photoURLs.length}/{MAX_PHOTOS}) · min {MIN_PHOTOS}</h2>
      <div className="photo-grid">
        {photoURLs.map((url) => (
          <div className="photo-cell" key={url}>
            <img src={url} alt="" />
            <button className="photo-x" onClick={() => setPhotoURLs((p) => p.filter((u) => u !== url))}>✕</button>
          </div>
        ))}
        {photoURLs.length < MAX_PHOTOS && (
          <label className="photo-cell photo-add">
            {uploading ? "…" : "+"}
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => onPickPhotos(e.target.files)}
            />
          </label>
        )}
      </div>

      <h2>I am</h2>
      <select value={gender} onChange={(e) => setGender(e.target.value as Gender)}>
        <option value="">Select…</option>
        {GENDERS.map((g) => (
          <option key={g.value} value={g.value}>{g.label}</option>
        ))}
      </select>

      <h2>Looking for</h2>
      <div className="chip-row">
        {GENDERS.map((g) => (
          <button
            key={g.value}
            className={"chip" + (seeking.includes(g.value) ? " chip-on" : "")}
            onClick={() => toggleSeeking(g.value)}
          >
            {g.label}
          </button>
        ))}
      </div>

      <h2>Three questions</h2>
      {prompts.map((p, i) => (
        <div key={i}>
          <select value={p.prompt} onChange={(e) => setPrompt(i, { prompt: e.target.value })}>
            <option value="">Choose question {i + 1}…</option>
            {PROMPT_BANK.filter((q) => q === p.prompt || !takenPrompts.has(q)).map((q) => (
              <option key={q} value={q}>{q}</option>
            ))}
          </select>
          {p.prompt && (
            <textarea
              value={p.answer}
              maxLength={150}
              placeholder="Your answer"
              onChange={(e) => setPrompt(i, { answer: e.target.value })}
            />
          )}
        </div>
      ))}

      <h2>Search radius</h2>
      <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
        {RADII.map((r) => (
          <option key={r} value={r}>{radiusLabel(r)}</option>
        ))}
      </select>

      <h2>Age range</h2>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label>Min</label>
          <input type="number" min={18} max={ageMax} value={ageMin} onChange={(e) => setAgeMin(Number(e.target.value))} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Max</label>
          <input type="number" min={ageMin} max={99} value={ageMax} onChange={(e) => setAgeMax(Number(e.target.value))} />
        </div>
      </div>

      <button className="btn" disabled={!formComplete || saving || uploading} onClick={save}>
        {saving ? "Saving…" : "Start matching"}
      </button>
      {!formComplete && (
        <p className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          Need: {MIN_PHOTOS}+ photos, name, birthday (18+), gender, who you're looking for, and all three questions.
        </p>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}
